import React, { useState, useEffect, useMemo } from 'react';
import { CardWrapper, Typography } from '../../components';
import { Chip } from '@heroui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import { CalendarIcon, DoubleArrowRightIc, EmptyContentIc } from '../../components/icons';
import { UniversalTable } from '../../components/AccountTable';
import CustomHeroButton from '../../components/button';
import CustomHeroInput from '../../components/CustomHeroInput';
import AlertBlock from '../../components/alert-block';
import partnerAuthService from '../../api/partner/partnerAuthService';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import WithdrawalHistoryModal from './components/WithdrawalHistoryModal';
import AddWalletModal from './components/AddWalletModal';
import WithdrawalSuccessModal from './components/WithdrawalSuccessModal';
import IdView from '../../components/id-view';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
// Типы для API ответа
interface BalanceHistoryItem {
  created_at: string;
  id: string;
  before: string;
  after: string;
  amount: string;
  transaction?: {
    amount: string;
    admin: string;
  };
  invoice?: {
    id: string;
    status: string;
  };
  previous: string;
  owner: string;
}

// Обновляем интерфейс для API ответа
interface BalanceHistoryResponse {
  page: number;
  page_size: number;
  count: number;
  objects: BalanceHistoryItem[];
}

// Тип для ответа создания платежного инвойса
interface PaymentInvoiceResponse {
  crypto_amount: string;
  crypto_currency: string;
  crypto_address: string;
  network: string;
  expiration_estimate_date: string;
}

// Тип для минимальных значений пополнения
interface MinimalTopUpAmount {
  crypto_currency: string;
  min_amount: number;
}

const Wallet: React.FC = () => {
  const { user, refreshUser, getBalance } = useAuth();
  const { t, lang } = useTranslations();
  const [balanceHistoryData, setBalanceHistoryData] = useState<BalanceHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<PaymentInvoiceResponse | null>(null);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false); // Флаг для предотвращения бесконечных запросов
  const [currentTime, setCurrentTime] = useState(new Date()); // Состояние для отслеживания текущего времени
  const breadcrumbs = useMemo(
    () => [
      { label: t('salesFans.home'), href: '/' },
      { label: t('sidebar.wallet'), isActive: true },
    ],
    [t]
  );

  usePageBreadcrumbs(breadcrumbs);
  // Состояния для модалок
  const [isWithdrawalHistoryOpen, setIsWithdrawalHistoryOpen] = useState(false);
  const [isAddWalletOpen, setIsAddWalletOpen] = useState(false);
  const [isWithdrawalSuccessOpen, setIsWithdrawalSuccessOpen] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [walletNetwork, setWalletNetwork] = useState<string | null>(null);
  const [withdrawalData, setWithdrawalData] = useState<{
    amount: number;
    created_at: string;
    address: string;
  } | null>(null);
  const [pendingAmount, setPendingAmount] = useState<number>(0);

  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<
    | {
        page: number;
        page_size: number;
        count: number;
      }
    | undefined
  >(undefined);

  const fetchBalanceHistory = async (page: number = 1, size: number = 10) => {
    if (hasError) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await partnerAuthService.getBalanceHistory({
        page: page,
        page_size: size,
      });
      if (response.data) {
        const data: BalanceHistoryResponse = response.data;
        setBalanceHistoryData(data.objects);
        setPagination({
          page: data.page,
          page_size: data.page_size,
          count: data.count,
        });
        setCurrentPage(data.page);
        setPageSize(data.page_size);
        setHasError(false);
      }
    } catch (error) {
      console.error('Failed to fetch balance history:', error);
      setError(t('wallet.failedLoadHistory'));
      setHasError(true); // Устанавливаем флаг ошибки
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingAmount = async () => {
    try {
      const response = await partnerAuthService.getPendingAmount();
      const rawData = response?.data;
      const amountCandidate =
        typeof rawData === 'number'
          ? rawData
          : (rawData?.amount ?? rawData?.pending_amount ?? rawData?.pendingAmount ?? 0);
      const numericAmount =
        typeof amountCandidate === 'number'
          ? amountCandidate
          : parseFloat(String(amountCandidate ?? '0'));
      setPendingAmount(Number.isFinite(numericAmount) ? numericAmount : 0);
    } catch (err) {
      console.error('Failed to fetch pending amount:', err);
      setPendingAmount(0);
    }
  };

  // Автообновление каждые 10 секунд только если нет ошибок
  useEffect(() => {
    fetchBalanceHistory(currentPage, pageSize); // Первоначальная загрузка

    const interval = setInterval(() => {
      if (!hasError) {
        // Проверяем флаг ошибки перед запросом
        fetchBalanceHistory(currentPage, pageSize);
      }
    }, 10000); // 10 секунд

    return () => clearInterval(interval);
  }, [hasError, currentPage, pageSize]);

  useEffect(() => {
    fetchPendingAmount();
  }, []);

  useEffect(() => {
    if (paymentInvoice) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());

        const date = new Date(paymentInvoice.expiration_estimate_date);
        if (date.getTime() <= currentTime.getTime()) {
          setPaymentInvoice(null);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [paymentInvoice, currentTime]);

  const createWithdrawalRequest = async (
    formAmount: number,
    formWallet: string,
    resetForm?: () => void
  ) => {
    const normalizedWallet = formWallet?.trim();
    if (!formAmount) {
      setInvoiceError(t('wallet.withdrawal.errors.noAmount'));
      return;
    }
    if (!normalizedWallet) {
      setInvoiceError(t('wallet.addWallet.errors.addressRequired'));
      return;
    }

    // if (!walletAddress) {
    //   setInvoiceError('Сначала добавьте кошелек для выплат');
    //   return;
    // }

    try {
      setIsCreatingInvoice(true);
      setInvoiceError(null);

      const response = await partnerAuthService.createWithdrawalOrder({
        amount: formAmount,
        description: t('wallet.withdrawal.requestDescription', {
          address: normalizedWallet || t('wallet.withdrawal.addressPlaceholder'),
        }),
        address: normalizedWallet,
      });

      if (response.data) {
        // Сохраняем данные для модалки
        setWithdrawalData({
          amount: response.data.amount,
          created_at: response.data.created_at,
          address: response.data.address,
        });

        // Показываем модалку успеха
        setIsWithdrawalSuccessOpen(true);

        // Обновляем баланс пользователя
        await getBalance();
        refreshUser();
        // Обновляем историю баланса
        fetchBalanceHistory(currentPage, pageSize);
        fetchPendingAmount();
        // Сбрасываем ошибку
        setInvoiceError(null);

        // Очищаем форму после успешного запроса
        if (resetForm) {
          resetForm();
        }
      }
    } catch (error: any) {
      console.error('Failed to create withdrawal request:', error);
      setInvoiceError(error?.response?.data?.message || t('wallet.withdrawal.errors.createFailed'));
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  const handleSaveWallet = (walletData: { address: string; network: string }) => {
    setWalletAddress(walletData.address);
    setWalletNetwork(walletData.network);
    // В реальном приложении здесь должен быть API запрос для сохранения кошелька
    // await partnerAuthService.saveWallet(walletData);
    setIsAddWalletOpen(false);
  };

  // Функция для определения типа действия
  const getActionType = (item: BalanceHistoryItem) => {
    const amount = parseFloat(item.amount);

    // Если есть транзакция
    if (item.transaction) {
      return amount >= 0 ? 'balance_top_up' : 'withdrawal';
    }

    // Если есть инвойс
    if (item.invoice) {
      const status = item.invoice.status?.toLowerCase();

      // Положительная сумма
      if (amount >= 0) {
        // Если статус refunded - это возврат средств
        if (status === 'refunded') {
          return 'refund';
        }
        // Иначе - пополнение баланса
        return 'balance_top_up';
      }

      // Отрицательная сумма - вывод средств
      return 'withdrawal';
    }

    // По умолчанию определяем по знаку суммы
    return amount >= 0 ? 'balance_top_up' : 'withdrawal';
  };

  // Функция для получения цвета суммы
  const getAmountColor = (amount: string) => {
    const numAmount = parseFloat(amount);
    return numAmount >= 0 ? 'text-auth-strength-strong' : 'text-auth-strength-weak';
  };

  const columns = useMemo(
    () => [
      { name: t('wallet.id'), key: 'id' },
      { name: t('wallet.amount'), key: 'amount' },
      { name: t('wallet.action'), key: 'action' },
      { name: t('wallet.date'), key: 'date' },
    ],
    [t]
  );
  const getStatusChip = (actionType: string) => {
    switch (actionType) {
      case 'wait_media':
        return {
          text: t('wallet.waitingMedia'),
          color: '#F31260' as const,
          variant: 'flat' as const,
        };
      case 'balance_top_up':
        return {
          text: t('wallet.balanceTopUpAction'),
          color: '#F5A524' as const,
          variant: 'flat' as const,
        };
      case 'withdrawal':
        return {
          text: t('wallet.withdrawal.action'),
          color: '#F31260' as const,
          variant: 'flat' as const,
        };
      case 'refund':
        return {
          text: t('wallet.refund'),
          color: '#17C964' as const,
          variant: 'flat' as const,
        };
      case 'invoice_payment':
        return {
          text: t('wallet.invoicePayment'),
          color: '#7828C8' as const,
          variant: 'flat' as const,
        };
      default:
        return {
          text: t('wallet.balanceChange'),
          color: '#7828C8' as const,
          variant: 'flat' as const,
        };
    }
  };
  const renderCell = (item: BalanceHistoryItem, columnKey: string) => {
    const actionType = getActionType(item);
    const statusChip = getStatusChip(actionType);
    switch (columnKey) {
      case 'id':
        return <IdView textToCopy={item.id}>{item.id.slice(-4)}</IdView>;
      case 'amount': {
        const isPositive = parseFloat(item.amount) >= 0;
        const sign = isPositive ? '+' : '';
        const formattedAmount = Math.abs(parseFloat(item.amount)).toFixed(2);
        const formattedBefore = parseFloat(item.before || '0').toFixed(2);
        const formattedAfter = parseFloat(item.after || '0').toFixed(2);

        return (
          <div className="flex flex-col gap-1">
            <span className={`${getAmountColor(item.amount)} text-sm font-semibold`}>
              {sign}${formattedAmount}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-auth-muted text-sm font-normal">${formattedBefore}</span>
              <DoubleArrowRightIc />
              <span className="text-auth-ink text-sm font-semibold">${formattedAfter}</span>
            </div>
          </div>
        );
      }
      case 'action':
        return (
          <Chip
            size="sm"
            style={{
              backgroundColor: `${statusChip.color}20`,
              color: statusChip.color,
            }}
          >
            {statusChip.text}
          </Chip>
        );
      case 'date':
        return (
          <div className="flex items-center gap-2">
            <CalendarIcon />
            <span className="text-auth-muted text-xs">{formatTimeAgo(item.created_at, t)}</span>
          </div>
        );
      default:
        return null;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchBalanceHistory(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchBalanceHistory(1, size);
  };
  // Схема валидации для формы
  const getValidationSchema = () =>
    Yup.object().shape({
      amount: Yup.number()
        .typeError(t('wallet.amountRequired'))
        .required(t('wallet.amountRequired'))
        .min(1, t('wallet.amountGreaterThanZero')),
      wallet: Yup.string().trim().required(t('wallet.addWallet.errors.addressRequired')),
    });

  const customEmptyContent = useMemo(
    () => (
      <div className="text-center py-12 w-full">
        <div className="flex flex-col  items-center gap-4">
          <EmptyContentIc />
          <div className="flex flex-col  max-w-[27.5rem] gap-1.5">
            <Typography variant="h6" className="text-xl font-semibold leading-7">
              {t('wallet.emptyHistoryTitle')}
            </Typography>
            <Typography
              className="text-neutral-500 text-base font-medium leading-6"
              variant="body1"
            >
              {t('wallet.emptyHistoryDescription')}
            </Typography>
          </div>
        </div>
      </div>
    ),
    [t]
  );

  return (
    <div className="flex flex-col h-full pb-4">
      {/* Header with title and button */}
      <div className="flex items-center justify-between px-4 md:px-6 py-4">
        <Typography variant="h5" className="text-auth-ink font-semibold text-2xl">
          {t('wallet.accountBalance')}
        </Typography>
        <CustomHeroButton
          title={t('wallet.withdrawal.historyButton')}
          onPress={() => setIsWithdrawalHistoryOpen(true)}
          className="!w-auto !px-4"
        />
      </div>

      <div className="flex h-full flex-col  gap-4 mt-4 px-6">
        {/* Balance Cards - Combined wrapper with divider */}
        <div className="flex md:flex-row flex-col w-full md:items-start md:justify-start">
          <div className="border border-tin border-solid flex md:flex-row flex-col md:items-center rounded-3.5">
            <div className="max-md:border-b md:border-r border-tin flex flex-col gap-1 p-4 md:w-[12.5rem]">
              <div className="flex flex-col h-5">
                <Typography
                  className="!text-neutral-800 !font-medium !text-sm !leading-[1.125rem]"
                  variant="body2"
                >
                  {t('wallet.currentBalance')}
                </Typography>
              </div>
              <div className="flex flex-col h-6 justify-center">
                <Typography
                  className="!font-semibold !text-neutral-900 !text-2xl !leading-6"
                  variant="h4"
                >
                  ${user?.balance?.toFixed(2) || '0.00'}
                </Typography>
              </div>
            </div>

            <div className="flex flex-col gap-1 p-4 md:w-[12.5rem]">
              <div className="flex flex-col h-5">
                <Typography
                  className="!text-neutral-800 !font-medium !text-sm !leading-[1.125rem]"
                  variant="body2"
                >
                  {t('wallet.withdrawal.requestedPayout')}
                </Typography>
              </div>
              <div className="flex flex-col h-6 justify-center">
                <Typography
                  className="!font-semibold !text-neutral-900 !text-2xl !leading-6"
                  variant="h4"
                >
                  ${pendingAmount.toFixed(2)}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        {/* Info Block - Wallet status */}
        {/* {walletAddress ? (
          <AlertBlock
            data={[
              {
                severity: "success",
                title: t('wallet.walletStatus.addedTitle'),
                description: `${walletAddress}${walletNetwork ? t('wallet.walletStatus.networkSuffix', { network: walletNetwork }) : ''}`
              }
            ]}
            addContent={
              <div className="mt-2">
                <CustomHeroButton
                  title={t('wallet.walletStatus.changeButton')}
                  onPress={() => setIsAddWalletOpen(true)}
                  variant="primary"
                  className="!w-auto !h-8 !px-3 !text-xs !rounded-2 !font-semibold"
                />
              </div>
            }
          />
        ) : (
          <AlertBlock
            data={[
              {
                severity: "info",
                title: t('wallet.walletStatus.notSelectedTitle'),
                descriptions: [t('wallet.walletStatus.notSelectedDescription')]
              }
            ]}
            addContent={
              <div className="mt-2">
                <CustomHeroButton
                  title={t('wallet.addWallet.addButton')}
                  onPress={() => setIsAddWalletOpen(true)}
                  variant="primary"
                  className="!w-auto !h-8 !px-3 !text-xs !rounded-2 !font-semibold"
                />
              </div>
            }
          />
        )} */}

        {/* Withdrawal Request Section */}
        <div className="flex flex-col gap-4 mt-4">
          <Typography variant="h6" className="text-auth-ink font-medium text-xl leading-6">
            {t('wallet.withdrawal.sectionTitle')}
          </Typography>
          <Formik
            initialValues={{ amount: '0', wallet: walletAddress || '' }}
            validationSchema={getValidationSchema()}
            onSubmit={(values, { resetForm }) => {
              createWithdrawalRequest(Number(values.amount), values.wallet, resetForm);
            }}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              submitForm,
              isSubmitting,
            }) => (
              <Form className="flex flex-col gap-4">
                <CardWrapper className="flex flex-col gap-4">
                  <CustomHeroInput
                    label={t('wallet.withdrawal.amountLabel')}
                    type="number"
                    name="amount"
                    value={values.amount}
                    onChange={(e) => {
                      setFieldValue('amount', e.target.value);
                    }}
                    onBlur={handleBlur}
                    className="max-w-[25rem]"
                    error={errors.amount}
                    touched={touched.amount}
                    description={t('wallet.withdrawal.amountDescription')}
                  />
                  <CustomHeroInput
                    label={t('wallet.addWallet.addressLabel')}
                    required
                    type="text"
                    name="wallet"
                    value={values.wallet}
                    onChange={(e) => {
                      setFieldValue('wallet', e.target.value);
                    }}
                    onBlur={handleBlur}
                    className="max-w-[25rem]"
                    error={errors.wallet}
                    touched={touched.wallet}
                    placeholder={t('wallet.addWallet.addressPlaceholder')}
                  />
                </CardWrapper>

                <div className="flex flex-col gap-2">
                  <CustomHeroButton
                    title={t('wallet.withdrawal.submitButton')}
                    onPress={() => submitForm()}
                    // disabled={isCreatingInvoice || isSubmitting || !walletAddress}
                    isLoading={isCreatingInvoice}
                    className="!w-[10rem]"
                  />

                  {invoiceError && (
                    <div className="text-auth-strength-weak text-sm">{invoiceError}</div>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>

        {/* Balance History Table */}
        <div className="flex flex-col gap-4 py-4 h-full">
          <Typography variant="h6" className="text-auth-ink font-medium text-xl leading-6">
            {t('wallet.balanceHistory')}
          </Typography>
          <UniversalTable
            mobileSize="sm"
            key={lang}
            data={balanceHistoryData}
            columns={columns}
            renderCell={renderCell}
            emptyContent={customEmptyContent}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            currentPageSize={pageSize}
          />
        </div>
      </div>

      {/* Модалки */}
      <WithdrawalHistoryModal
        isOpen={isWithdrawalHistoryOpen}
        onClose={() => setIsWithdrawalHistoryOpen(false)}
      />

      <AddWalletModal
        isOpen={isAddWalletOpen}
        onClose={() => setIsAddWalletOpen(false)}
        onSave={handleSaveWallet}
      />

      <WithdrawalSuccessModal
        isOpen={isWithdrawalSuccessOpen}
        onClose={() => setIsWithdrawalSuccessOpen(false)}
        withdrawalData={withdrawalData}
      />
    </div>
  );
};

export default Wallet;
